import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const TimelineContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;
  
  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const DateHeader = styled.h2`
  text-align: center;
  font-size: 2rem;
  margin: 3rem 0 2rem;
  padding: 1rem;
  background-color: ${props => props.theme.colors.light};
  border-radius: 8px;
  color: ${props => props.theme.colors.primary};
`;

const TimelineEvents = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 2rem;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(to bottom, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      left: 1rem;
    }
  }
`;

const TimelineEvent = styled(motion.div)`
  position: relative;
  margin-bottom: 2rem;
  padding-left: 5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-left: 3rem;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 0.5rem;
  top: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${props => {
    switch(props.type) {
      case 'emergency': return props.theme.colors.accent;
      case 'lifesaving': return props.theme.colors.success;
      case 'medical': return props.theme.colors.warning;
      case 'hospital': return props.theme.colors.secondary;
      case 'rescue': return '#ff6b35';
      case 'recovery': return '#4ecdc4';
      case 'spiritual': return '#9b59b6';
      case 'community': return '#f39c12';
      case 'warning': return '#e67e22';
      case 'normal': return props.theme.colors.gray;
      default: return props.theme.colors.gray;
    }
  }};
  border: 4px solid white;
  box-shadow: 0 0 0 4px ${props => {
    switch(props.type) {
      case 'emergency': return props.theme.colors.accent;
      case 'lifesaving': return props.theme.colors.success;
      case 'medical': return props.theme.colors.warning;
      case 'hospital': return props.theme.colors.secondary;
      case 'rescue': return '#ff6b35';
      case 'recovery': return '#4ecdc4';
      case 'spiritual': return '#9b59b6';
      case 'community': return '#f39c12';
      case 'warning': return '#e67e22';
      case 'normal': return props.theme.colors.gray;
      default: return props.theme.colors.gray;
    }
  }}40;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    left: 0;
  }
`;

const EventCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => {
    switch(props.type) {
      case 'emergency': return props.theme.colors.accent;
      case 'lifesaving': return props.theme.colors.success;
      case 'medical': return props.theme.colors.warning;
      case 'hospital': return props.theme.colors.secondary;
      case 'rescue': return '#ff6b35';
      case 'recovery': return '#4ecdc4';
      case 'spiritual': return '#9b59b6';
      case 'community': return '#f39c12';
      case 'warning': return '#e67e22';
      case 'normal': return props.theme.colors.gray;
      default: return props.theme.colors.gray;
    }
  }};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

const EventTime = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  font-size: 1.3rem;
`;

const EventDescription = styled.p`
  color: ${props => props.theme.colors.dark};
  line-height: 1.6;
  margin-bottom: 0;
`;

const EventType = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 1rem;
  background-color: ${props => {
    switch(props.type) {
      case 'emergency': return props.theme.colors.accent;
      case 'lifesaving': return props.theme.colors.success;
      case 'medical': return props.theme.colors.warning;
      case 'hospital': return props.theme.colors.secondary;
      case 'rescue': return '#ff6b35';
      case 'recovery': return '#4ecdc4';
      case 'spiritual': return '#9b59b6';
      case 'community': return '#f39c12';
      case 'warning': return '#e67e22';
      case 'normal': return props.theme.colors.gray;
      default: return props.theme.colors.gray;
    }
  }};
  color: white;
`;

const NextSectionContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin: 3rem auto 2rem auto;
  text-align: center;
  max-width: 600px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
`;

const NextSectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

const Timeline = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Static timeline data from PRD
  const thanksgivingDayEvents = [
    {
      time: "7:00–9:00 AM",
      event: "Scott and Isaac visit Israel Canyon for Isaac's .22 rifle practice; return to prepare for Thanksgiving and Goblin Valley campout (Nov. 29–Dec. 1) with Isaac, Noah, Declan, and Ian. Scott researches Dutch oven soup recipes.",
      type: "normal"
    },
    {
      time: "12:00 PM",
      event: "Scott plans campout meals in the kitchen. Maggie, we later learned by checking her Fitbit history that during the time of her incident she had an elevated heart rate, joins him in the pantry to select three large potatoes.",
      type: "warning"
    },
    {
      time: "12:20 PM",
      event: "Maggie hands Scott the third potato, says, 'I feel like I'm going to pass out,' collapses onto his gym shoe, seizes with wretched fingers/toes, face gray, eyes rolled back. Scott panics, nudges her, calls her name, feeling dread and shock.",
      type: "emergency"
    },
    {
      time: "12:21 PM",
      event: "Scott attempts CPR and mouth-to-mouth, realizing no breathing or heartbeat, incapacitated but acting. Scott calls for Eliza to come, (Scott remembers a conversation a week earlier when Eliza told him that she just recertified in CPR at MTECH), Isaac calls 911. Noah shouts, 'Eliza, Mom needs CPR!' Eliza rushes down in a towel.",
      type: "emergency"
    },
    {
      time: "12:22 PM",
      event: "Eliza taps Scott's shoulder and says 'move, move, move!', checks pulse/respiration (none), begins 30 chest compressions for nearly 5 minutes. Isaac hands Scott the phone; dispatcher counts '1, 2, 3, 4,' and asked us to check for breathing. Eliza begins compressions and counts to 30. Maggie moans. Noah gives Eliza his shirt, Scott puts it on her. Dispatcher instructs unlocking door, securing pets.",
      type: "lifesaving"
    },
    {
      time: "12:23 PM",
      event: "**Noah's Prayer Mobilization**: Noah, with genuine heartache and real concern, begins sending this text message to hundreds of contacts: 'Please pray for my mom, we think she just had a heart attack.' His immediate prayer mobilization effort creates a network of faith that stretches across states.",
      type: "spiritual"
    },
    {
      time: "12:25 PM",
      event: "Nine first responders (EMTs, police, firefighters) arrive within 5 minutes, led by a woman. Isaac shouts, 'She's in here!' EMTs take over after Eliza's compressions. Noah hands Scott pants for Eliza, Isaac shuts pantry for her to dress.",
      type: "rescue"
    },
    {
      time: "12:26–12:50 PM",
      event: "Responders slide Maggie to kitchen, move table, cop does compressions, electrode defibrillation (3 shocks). Lady cop escorts Scott/Eliza to front room. Officer updates; Maggie's moans (every few seconds) captured by refrigerator camera. After 26 minutes, weak pulse detected; loaded onto gurney. EMT offers Scott a ride; he stays to change/brush teeth.",
      type: "medical"
    },
    {
      time: "12:50–1:00 PM",
      event: "Isabel and Declan arrive, distraught (Eliza said heart attack). Scott and Isabel hug on couch, she sobs. Heather Jordan and Kirsten Hall care for kids in mud room. 20–25 neighbors (Aaron Jacob, Lance Dome, Tiffany Bryant, others) stand outside. Eliza offers to drive; Scott, Eliza, Isaac leave for Holy Cross Hospital - Mountain Point.",
      type: "community"
    },
    {
      time: "1:00–2:00 PM",
      event: "Scott calls Ben, cancels Thanksgiving, shares emergency. Bishop Clint Nelson meets them at hospital. ER nurse confirms ventilator/pulse. Scott anoints, Bishop blesses with Ron. Chaplain Dave offers comfort. Maggie (breathing tube, femoral/arm IVs) lifeless; Scott blesses Noah, Lindi present.",
      type: "hospital"
    },
    {
      time: "Evening",
      event: "Maggie moves to ICU; Dr. Ghali, Dr. Al Jandali, Nurse Andrew care for her. Scott fears brain damage.",
      type: "hospital"
    }
  ];

  const recoveryWeekEvents = [
    {
      date: "Nov. 29 (Fri)",
      event: "4:00 PM: Doctors report Maggie is doing really well. They plan to provide food through the tube and wean her off some drugs to reduce sedation (she cannot breathe on her own while sedated), preparing to remove the breathing tube possibly tomorrow. Maggie communicates with eyebrows, squeezes hands, and wiggles toes when asked. Scott notes, 'I think she's gonna be OK.' Visitors: Ron, Marlese, Lee, Ingrid, Brit, Bethany. ",
      type: "recovery"
    },
    {
      date: "Nov. 29 (Fri) - 6:00 PM",
      event: "Maggie in her sedated state still attempts multiple times to pull her breathing tube out, the nurse straps her arms down so she can't, however Maggie did not give up and persisted and eventually by sitting up, all while still sedated, pulls her breathing tube out which damaged her windpipe.**Ingrid's First Conversation**: Ingrid becomes the first person to have a meaningful conversation with Maggie upon waking, discussing Maggie's near-death experience. Maggie told Ingrid that she spoke with her deceased mother Becky. Maggie also said she saw her deceased brother Lars in the room with her.Maggie told Ingrid that she had a repeatedly had the same dram that she died and was told to go back.",
      type: "medical"
    },
    {
      date: "Nov. 30 (Sat)",
      event: "Maggie begins regaining short-term memory. Doctors plan to get her moving, possibly on solid food, and evaluate her throat; her throat was sore and she was very hoarse (and would remain hoarse for 6 weeks to follow), no discharge today. Maggie walks around the ICU floor, uses the bathroom independently, and has visits all day. She's loopy from propofol sedative with spotty short-term memory but improving. Part of her heart is not functioning properly, causing arrhythmia; cardiologist consultation scheduled for Monday.",
      type: "recovery"
    },
    {
      date: "Dec. 1 (Sun)",
      event: "Maggie is doing good, still slightly loopy with short-term memory not fully returned but better than yesterday. Cardiologist confirms the apex of her heart is not working, which caused atrial fibrillation (AFib) two additional times while in the hospital. Options for short-term or long-term solutions discussed.",  
      type: "medical"
    },
    {
      date: "Dec. 2 (Mon)",
      event: "Maggie left Holy Cross Hospital - Mountain Point and was transported via ambulance to St. Mark's Hospital. 2:13 PM: Family arrives at St. Mark's, room 5N22. Maggie is doing great, with most short-term memory back. Doctors note her arm will be in a sling for a few weeks post-ICD surgery, but the exact surgery date is unclear until evaluation at St. Mark's. Family blessing performed. Cognitive test passed (Camrin Terry), ICD surgery planned with Dr. John Day at St. Mark's.",
      type: "medical"
    },
    {
      date: "Dec. 3 (Tue)",
      event: "Maggie has another echocardiogram; the previously akinetic region is now a small area, though prior records are unavailable for comparison. MRI of her heart scheduled at 4:00 PM. ICD surgery planned for tomorrow. ICD placed in Maggie's chest. MRI/echo confirm takotsubo cardiomyopathy, meets PA Jared Simpson, nurse Larissah.",
      type: "medical"
    },
    {
      date: "Dec. 4 (Wed)",
      event: "ICD surgery begins at 6:10 PM, completes at 8:20 PM, reported as successful by Dr. Nannapaneni. Blessing by Ben Lambert. Ingrid talks to Maggie. ICD surgery (6:49–8:15 PM) by Dr. Nannapaneni, blessing by Ben Lambert. Ingrid talks to Maggie.",
      type: "medical"
    },
    {
      date: "Dec. 5 (Thu)",
      event: "Nurse initiates discharge papers. Family ride arrives at 3:00 PM, expects to be home by 4:00 PM. Maggie cleared to checkout and discharged. Eliza drove to St. Mark's Hospital to bring Scott and Maggie home.",
      type: "recovery"
    }
  ];

  const getEventIcon = (type) => {
    switch(type) {
      case 'emergency': return '🚨';
      case 'lifesaving': return '💗';
      case 'medical': return '🏥';
      case 'hospital': return '🩺';
      case 'rescue': return '🚑';
      case 'recovery': return '✨';
      case 'spiritual': return '🙏';
      case 'community': return '🤝';
      case 'warning': return '⚠️';
      default: return '📅';
    }
  };

  const getFilteredEvents = () => {
    if (selectedFilter === 'all') return thanksgivingDayEvents;
    return thanksgivingDayEvents.filter(event => event.type === selectedFilter);
  };

  const FilterButton = styled.button`
    padding: 0.5rem 1rem;
    margin: 0.25rem;
    border: 2px solid ${props => props.theme.colors.primary};
    background: ${props => props.active ? props.theme.colors.primary : 'white'};
    color: ${props => props.active ? 'white' : props.theme.colors.primary};
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      color: white;
    }
  `;

  const FilterContainer = styled.div`
    text-align: center;
    margin: 2rem 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  `;

  return (
    <TimelineContainer>
      <SEO 
        title="Timeline of Events - November 28, 2024"
        description="Hour-by-hour timeline of Maggie's cardiac arrest and miraculous recovery. From 12:20 PM collapse to 26 minutes of CPR to pulse return - every critical moment documented in this detailed emergency response timeline."
        url="/timeline"
        image="/uploads/EMTS_AT_WORK.jpg"
        type="article"
      />
      <PageHeader>
        <PageTitle>Timeline of Events</PageTitle>
        <PageSubtitle>
          A detailed, hour-by-hour account of November 28, 2024 - 
          the day that changed everything for the Soward family
        </PageSubtitle>
      </PageHeader>

      <FilterContainer>
        <FilterButton 
          active={selectedFilter === 'all'} 
          onClick={() => setSelectedFilter('all')}
        >
          All Events
        </FilterButton>
        <FilterButton 
          active={selectedFilter === 'emergency'} 
          onClick={() => setSelectedFilter('emergency')}
        >
          🚨 Emergency
        </FilterButton>
        <FilterButton 
          active={selectedFilter === 'lifesaving'} 
          onClick={() => setSelectedFilter('lifesaving')}
        >
          💗 Life-Saving
        </FilterButton>
        <FilterButton 
          active={selectedFilter === 'medical'} 
          onClick={() => setSelectedFilter('medical')}
        >
          🏥 Medical
        </FilterButton>
        <FilterButton 
          active={selectedFilter === 'spiritual'} 
          onClick={() => setSelectedFilter('spiritual')}
        >
          🙏 Spiritual
        </FilterButton>
      </FilterContainer>

      <DateHeader>Thanksgiving Day - November 28, 2024</DateHeader>
      
      <TimelineEvents>
        {getFilteredEvents().map((event, index) => (
          <TimelineEvent
            key={index}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <TimelineDot type={event.type}>
              {getEventIcon(event.type)}
            </TimelineDot>
            <EventCard type={event.type}>
              <EventType type={event.type}>{event.type.toUpperCase()}</EventType>
              <EventTime>{event.time}</EventTime>
              <EventDescription dangerouslySetInnerHTML={{__html: event.event}} />
            </EventCard>
          </TimelineEvent>
        ))}
      </TimelineEvents>

      <DateHeader>Recovery Week - November 29 - December 5, 2024</DateHeader>
      <TimelineEvents>
        {recoveryWeekEvents.map((event, index) => (
          <TimelineEvent
            key={`recovery-${index}`}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <TimelineDot type={event.type}>
              {getEventIcon(event.type)}
            </TimelineDot>
            <EventCard type={event.type}>
              <EventType type={event.type}>{event.type.toUpperCase()}</EventType>
              <EventTime>{event.date}</EventTime>
              <EventDescription dangerouslySetInnerHTML={{__html: event.event}} />
            </EventCard>
          </TimelineEvent>
        ))}
      </TimelineEvents>

      <NextSectionContainer>
        {/* <NextSectionText>
          You've explored the complete timeline of this miracle. Return to the beginning to share this story...
        </NextSectionText> */}
        <NextSectionButton href="/">
          🏠 Return to Home →
        </NextSectionButton>
      </NextSectionContainer>
    </TimelineContainer>
  );
};

export default Timeline;
